import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileX, DownloadSimple } from "@phosphor-icons/react";

/**
 * Fetches a source document as a blob (authenticated) and shows it in an iframe
 * via an object URL. Also renders a Download button so users can always save the
 * original. If the doc has been wiped, shows a friendly placeholder — no more
 * raw JSON leaking into the preview.
 */
export default function SourceDocViewer({ documentId, height = 540, filename }) {
    const [state, setState] = useState({ loading: true, exists: false, blobUrl: null, contentType: null });

    useEffect(() => {
        if (!documentId) {
            setState({ loading: false, exists: false, blobUrl: null, contentType: null });
            return;
        }
        let cancelled = false;
        let createdUrl = null;
        (async () => {
            try {
                const res = await api.get(`/documents/${documentId}/download`, {
                    responseType: "blob",
                    validateStatus: () => true,
                });
                if (cancelled) return;
                if (res.status !== 200) {
                    setState({ loading: false, exists: false, blobUrl: null, contentType: null });
                    return;
                }
                createdUrl = URL.createObjectURL(res.data);
                setState({ loading: false, exists: true, blobUrl: createdUrl, contentType: res.data.type || "application/octet-stream" });
            } catch (_e) {
                if (!cancelled) setState({ loading: false, exists: false, blobUrl: null, contentType: null });
            }
        })();
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [documentId]);

    const download = () => {
        if (!state.blobUrl) return;
        const a = document.createElement("a");
        a.href = state.blobUrl;
        a.download = filename || "document";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    if (!documentId) {
        return <Placeholder height={height} title="No source document" hint="This entry has no attached file." />;
    }
    if (state.loading) {
        return (
            <div className="w-full flex items-center justify-center text-stone-400 text-sm border rounded-sm"
                 style={{ height, borderColor: "var(--se-border)" }}
                 data-testid="source-doc-loading">
                Loading source document…
            </div>
        );
    }
    if (!state.exists) {
        return (
            <Placeholder height={height} title="Source document not available"
                hint="The original file was cleared from storage. The data below is unaffected — you can still edit and re-post." />
        );
    }
    const isImage = (state.contentType || "").startsWith("image/");
    return (
        <div className="space-y-2" data-testid="source-doc-ok">
            <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={download} data-testid="source-doc-download">
                    <DownloadSimple size={14} className="mr-1.5" /> Download
                </Button>
            </div>
            {isImage ? (
                <img
                    src={state.blobUrl}
                    alt="source-document"
                    className="w-full rounded-sm border object-contain bg-white"
                    style={{ maxHeight: height, borderColor: "var(--se-border)" }}
                    data-testid="source-doc-iframe"
                />
            ) : (
                <iframe
                    title="source-document"
                    src={state.blobUrl}
                    className="w-full rounded-sm border bg-white"
                    style={{ height, borderColor: "var(--se-border)" }}
                    data-testid="source-doc-iframe"
                />
            )}
        </div>
    );
}

function Placeholder({ height, title, hint }) {
    return (
        <div
            className="w-full flex flex-col items-center justify-center text-center border rounded-sm p-6"
            style={{ height, borderColor: "var(--se-border)", background: "var(--se-canvas, #fafaf9)" }}
            data-testid="source-doc-missing"
        >
            <div className="text-stone-400 mb-3"><FileX size={28} /></div>
            <div className="text-sm font-medium text-stone-600">{title}</div>
            <div className="text-xs text-stone-500 mt-1 max-w-xs">{hint}</div>
        </div>
    );
}
